// frontend/src/context/AuthContext.tsx

import React, { createContext, useContext, useEffect, useState } from "react";
import { API_CONFIG } from '../utils/config';
import logger from '../utils/logger';
import { checkSession, isSessionUnavailableError, type SessionUnavailableError } from "../utils/checkSession";
import { clearUserCache } from '../utils/cacheManager';
import { safeSetItem } from '../utils/safeStorage';
import { VERIFIED_ACCESS_CHANGED_EVENT } from '../utils/verifiedAccess';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  clearAppSessionToken,
  getAppSessionCredentials,
  storeAppSessionToken,
} from '../utils/session';

export interface User {
  // Identity
  id?: string;
  canonicalUserId?: string;
  wixAccountId?: string;
  memberId?: string;
  contactId?: string;
  email?: string;
  loginEmail?: string;
  
  // Profile
  name?: string;
  profile?: {
    nickname?: string;
    slug?: string;
    photo?: {
      url: string;
      height: number;
      width: number;
    };
  };
  
  // Contact Details
  contact?: {
    firstName?: string;
    lastName?: string;
    emails?: Array<{
      tag: string;
      email: string;
      primary?: boolean;
    }>;
    phones?: Array<{
      tag: string;
      phone: string;
      primary?: boolean;
    }>;
    addresses?: Array<{
      tag: string;
      address: any;
    }>;
    company?: string;
    jobTitle?: string;
  };
  
  // Authorization
  roles?: string[];
  isAdmin?: boolean;
  
  // Subscription
  plan?: string;
  planName?: string;
  benefits?: string[];
  accessResolution?: 'verified' | 'program_courtesy_verified' | 'unavailable';
  accessSource?: string;
  accessSourceRef?: string | null;
  programCourtesy?: {
    phase: 'active' | 'grace' | 'expired';
    organizationIds: number[];
    licenseIds: number[];
    seatIds: number[];
    accessStartsAt?: string | null;
    accessEndsAt: string | null;
    graceEndsAt: string | null;
  };
  paidDowngradeRecovery?: {
    phase: 'recoverable' | 'expired' | 'unavailable';
    recoveryEndsAt: string | null;
    recoverableItems: number | null;
    expiredItems: number | null;
  };
  
  // Custom Fields
  customFields?: Record<string, any>;
}

interface AuthContextProps {
  isLoggedIn: boolean;
  user: User | null;
  login: (
    email: string,
    password: string,
    options?: { sessionToken?: string; plan?: string; cookieSession?: boolean }
  ) => Promise<User>;
  logout: (options?: { redirect?: boolean }) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const ACCESS_RESOLUTION_ERROR_CODES = new Set([
  'ACCESS_IDENTITY_UNAVAILABLE',
  'VERIFIED_ACCESS_SYNC_FAILED',
  'WIX_PLAN_MAPPING_UNAVAILABLE',
]);

const optionalIdentityId = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() ? value.trim() : undefined
);

const projectUnavailableAccessUser = (
  error: SessionUnavailableError,
  currentUser: User | null,
): User | null => {
  if (!error.code || !ACCESS_RESOLUTION_ERROR_CODES.has(error.code)) return null;

  const source = error.user?.id || error.user?.memberId || error.user?.contactId
    ? error.user
    : currentUser;
  if (!source?.id && !source?.memberId && !source?.contactId) return null;

  const {
    plan: _plan,
    planName: _planName,
    benefits: _benefits,
    accessSource: _accessSource,
    accessSourceRef: _accessSourceRef,
    programCourtesy: _programCourtesy,
    paidDowngradeRecovery: _paidDowngradeRecovery,
    ...identity
  } = source;

  return {
    ...identity,
    id: optionalIdentityId(identity.canonicalUserId) || optionalIdentityId(identity.id),
    wixAccountId: optionalIdentityId(identity.wixAccountId) || optionalIdentityId(identity.id),
    benefits: [],
    accessResolution: 'unavailable',
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // PRODUCTION MODE: Never trust localStorage, always verify with backend
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = API_CONFIG.BACKEND_URL;

  // forced re-auth via URL
  useEffect(() => {
    if (window.location.search.includes("forceReload=true")) {
      logger.debug("🔄 Forcing re-auth after logout…");
      localStorage.setItem("loggedIn", "false");
      setIsLoggedIn(false);
      setUser(null);
      setTimeout(fetchSession, 500);
    }
  }, []);

  // Retry a backend-verified session check when connectivity returns.
  useEffect(() => {
    void fetchSession();
    const handleOnline = () => void fetchSession();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // ⭐ AUTO-SYNC: Ensure localStorage.userId stays synced with session
  useEffect(() => {
    if (user && user.id) {
      const storedUserId = localStorage.getItem('userId');
      
      // If userId not set or different, sync it
      if (storedUserId !== user.id) {
        logger.debug('Refreshing the cached canonical account reference');
        localStorage.setItem('userId', user.id);
        
        // Also sync email if available
        const email = user.email || user.loginEmail;
        if (email) {
          localStorage.setItem('userEmail', email);
        }
      }
    }
  }, [user]); // Re-run whenever user changes

  async function fetchSession(requireVerified = false): Promise<User | null> {
    logger.debug("🍪 Checking session via checkSession()");
    setLoading(true);

    let sessionData;
    try {
      sessionData = await checkSession();
    } catch (err) {
      if (isSessionUnavailableError(err)) {
        const unavailableUser = projectUnavailableAccessUser(err, user);
        if (unavailableUser) {
          const email = unavailableUser.email || unavailableUser.loginEmail || '';
          logger.warn('Commercial access could not be verified; preserving identity without a plan.');
          setIsLoggedIn(true);
          setUser(unavailableUser);
          safeSetItem('loggedIn', 'true');
          localStorage.removeItem('userPlan');
          if (unavailableUser.id) safeSetItem('userId', unavailableUser.id);
          if (email) safeSetItem('userEmail', email);
          else localStorage.removeItem('userEmail');
          window.dispatchEvent(new CustomEvent(VERIFIED_ACCESS_CHANGED_EVENT));
          setLoading(false);
          if (requireVerified) throw err;
          return unavailableUser;
        }
        logger.warn('Session check unavailable; keeping the current authenticated state.');
      } else {
        logger.error('Unexpected session check failure; keeping the current authenticated state.', err);
      }
      setLoading(false);
      if (requireVerified) throw err;
      return user;
    }

    if (sessionData?.id) {
      const plan = typeof sessionData.plan === 'string' && sessionData.plan.trim()
        ? sessionData.plan.trim()
        : undefined;
      const email = sessionData.email || sessionData.loginEmail || '';
      
      logger.debug('Verified session projection received', {
        hasCanonicalIdentity: Boolean(sessionData.canonicalUserId || sessionData.id),
        hasEmail: Boolean(email),
        hasName: Boolean(sessionData.name),
        hasPlan: Boolean(plan),
      });
      
      setIsLoggedIn(true);
      const userObject: User = {
        ...sessionData,
        id: sessionData.canonicalUserId || sessionData.id,
        wixAccountId: sessionData.wixAccountId || sessionData.id,
        email,
        benefits: sessionData.benefits || [],
        ...(plan ? { plan } : {}),
      };
      logger.debug('Applying verified session projection to the authenticated context');
      setUser(userObject);
      window.dispatchEvent(new CustomEvent(VERIFIED_ACCESS_CHANGED_EVENT));
      
      // Store for admin detection and API calls
      safeSetItem("loggedIn", "true");
      if (plan) {
        safeSetItem("userPlan", plan);
      } else {
        localStorage.removeItem("userPlan");
      }
      if (sessionData.id) {
        safeSetItem("userId", sessionData.id);
      }
      if (email) {
        safeSetItem("userEmail", email);
      } else {
        localStorage.removeItem("userEmail");
      }
      
      setLoading(false);
      return userObject;
    }

    logger.warn("🛑 No valid session");
    setIsLoggedIn(false);
    setUser(null);
    localStorage.setItem("loggedIn", "false");
    localStorage.removeItem("userPlan");
    localStorage.removeItem("userId");
    localStorage.removeItem("userEmail");
    setLoading(false);
    return null;
  }

  const login = async (
    email: string,
    password: string,
    options?: { sessionToken?: string; plan?: string; cookieSession?: boolean }
  ) => {
    // sessionToken-only flow (OAuth / magic link)
    if (options?.sessionToken || options?.cookieSession) {
      logger.debug("🔐 Verifying the newly established session…");
      if (options.sessionToken) storeAppSessionToken(options.sessionToken);

      // The caller received this app-signed token from the backend. Verify it
      // through the read-only session endpoint instead of replaying loginUser.
      const authenticatedUser = await fetchSession(true);
      if (!authenticatedUser?.id) {
        throw new Error("Authenticated identity could not be verified");
      }
      return authenticatedUser;
    }

    // email/password flow
    const res = await fetch(`${API_BASE}/api/loginUser`, {
      method: "POST",
      credentials: getAppSessionCredentials(),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || "Login failed");
    }
    if (typeof data.sessionToken === 'string' && data.sessionToken.trim()) {
      storeAppSessionToken(data.sessionToken);
    }

    // Give browser time to process Set-Cookie headers before checking session
    await new Promise((r) => setTimeout(r, 300));
    const authenticatedUser = await fetchSession(true);
    if (!authenticatedUser?.id) {
      throw new Error("Authenticated identity could not be verified");
    }

    setIsLoggedIn(true);
    localStorage.setItem("loggedIn", "true");

    return authenticatedUser;
  };

  const logout = async (options?: { redirect?: boolean }) => {
    logger.debug("🔁 Logging out via backend…");
    const response = await fetchWithAuth(`${API_BASE}/api/logout`, {
      method: "POST",
    });
    if (!response.ok) {
      throw new Error("Logout failed");
    }

    // Clear all user-scoped cache and storage (E4 fix)
    clearUserCache();
    clearAppSessionToken();
    
    localStorage.setItem("loggedIn", "false");
    setIsLoggedIn(false);
    setUser(null);

    if (options?.redirect === false) return;

    const next = "https://app.XPROJEX.com";
    window.location.href = `https://www.XPROJEX.com/logout-bridge?next=${encodeURIComponent(
      next
    )}`;
  };

  return (
    <AuthContext.Provider
      value={{ isLoggedIn, user, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
